// M5.9: Admin manual grant (XP + coins) modal.
// See ADR-0017 (.ai/decisions/0017-admin-manual-grant.md)
//
// The admin can grant:
//   - XP to specific competencies (one row per competency
//     in the splits array)
//   - Coins to the user's balance
//   - Both in a single grant
//
// Each grant requires a 'reason' (10-500 chars) which is
// the audit trail's most-searchable field. The grant is
// recorded in:
//   - xp_award (one row per competency, source='admin_grant')
//   - coin_transaction (one row, kind='admin_grant', if coins > 0)
//   - audit_log (one row, action='admin.grant_xp_coins')
// All in a single transaction so a partial grant can't
// leave the user in an inconsistent state.
//
// The bypasses is_ignored intentionally: the whole point
// of the grant is to override the system for a user.
// If the user has is_ignored=true, the grant still
// credits XP/coins. The audit log captures the action.

import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface User {
  id: string;
  handle: string;
  display_name: string;
  email: string;
  is_ignored: boolean;
  // ... other fields not used in this modal
}

interface Competency {
  id: string;
  name: string;
  slug: string;
}

interface XpEntry {
  competency_id: string;
  amount: number;
}

interface GrantForm {
  xp: XpEntry[];
  coins: number;
  reason: string;
}

const emptyForm = (firstCompetencyId: string): GrantForm => ({
  // M5.9 UX fix: pre-populate ONE XP row so the admin
  // doesn't have to click "+ Add XP row" before they can
  // see the row's competency dropdown + amount input.
  // The default amount is 5 (matches the daily-quest
  // template's typical XP) and the first competency in
  // the catalog. The admin can change either field, add
  // more rows, or remove this one with the ✕ button.
  // The amount=0 case is still handled (the submit
  // filter and the api validator both reject zero
  // amounts).
  xp: firstCompetencyId ? [{ competency_id: firstCompetencyId, amount: 5 }] : [],
  coins: 0,
  reason: '',
});

export function GrantXpCoinsModal({ target, onClose }: { target: User; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<GrantForm>(() => emptyForm(''));
  const [err, setErr] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch the competency list so the form can show
  // <select> dropdowns for each XP row. The api returns
  // id + name; we use both in the option label.
  const competencies = useQuery<{ competencies: Competency[] }>({
    queryKey: ['competencies'],
    queryFn: () => api('/api/competencies'),
    staleTime: 60_000,
  });
  // v0.6.2: stable local. The previous code used
  // `competencies.data!.competencies[0]!.id` on the
  // 'add xp row' effect / button. The `!` on `data`
  // asserts data is non-null, but if `data` is `{}`
  // (no `competencies` field), then `data.competencies`
  // is undefined and `[0]` crashes. The earlier commit
  // (a47ac3d) wrapped the `data?.competencies.length`
  // checks but missed the `data!.competencies[0]` calls
  // inside the closures. The local default-to-[] and
  // the safe `?.[0]?.id ?? ''` cover both.
  const competenciesList = competencies.data?.competencies ?? [];

  // M5.9 UX fix: as soon as the competency list arrives,
  // populate the first XP row with the first competency
  // (if the form was empty). The default amount is 5.
  // This effect is a one-shot: once the first row is
  // populated, the user controls the rows via the +
  // Add / ✕ buttons. The xp.length check avoids
  // clobbering user edits if they manually cleared
  // the row.
  useEffect(() => {
    if (competenciesList.length && form.xp.length === 0) {
      const firstId = competenciesList[0]?.id ?? '';
      setForm((f) => ({
        ...f,
        xp: [{ competency_id: firstId, amount: 5 }],
      }));
    }
  }, [competenciesList, form.xp.length]);

  const grant = useMutation({
    mutationFn: (body: { xp: XpEntry[]; coins: number; reason: string }) =>
      api(`/api/admin/users/${target.id}/grant`, { method: 'POST', body }),
    onSuccess: () => {
      // Invalidate everything that might show stale data:
      // - user state (total_xp, per-competency, coin balance)
      // - audit log (so the admin sees their own grant)
      qc.invalidateQueries({ queryKey: ['me-progress'] });
      qc.invalidateQueries({ queryKey: ['me-coins'] });
      qc.invalidateQueries({ queryKey: ['me-coin-transactions'] });
      qc.invalidateQueries({ queryKey: ['me-events'] });
      qc.invalidateQueries({ queryKey: ['admin-audit-log'] });
      onClose();
    },
  });

  function addXpRow() {
    if (!competenciesList.length) return;
    const firstId = competenciesList[0]?.id ?? '';
    setForm((f) => ({
      ...f,
      xp: [...f.xp, { competency_id: firstId, amount: 5 }],
    }));
  }

  function removeXpRow(idx: number) {
    setForm((f) => ({ ...f, xp: f.xp.filter((_, i) => i !== idx) }));
  }

  function updateXpRow(idx: number, patch: Partial<XpEntry>) {
    setForm((f) => ({
      ...f,
      xp: f.xp.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    // Client-side guard. The form-level validation is
    // duplicated here so the error message is shown
    // inline (the button being disabled hides it).
    if (!reasonValid) {
      setErr('Reason must be at least 10 characters — this is the audit trail.');
      return;
    }
    if (!hasValidAmount) {
      setErr('Add at least one XP row with a positive amount, or a positive coin amount.');
      return;
    }
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }
    grant.mutate({
      xp: form.xp.filter((r) => r.amount > 0),
      coins: form.coins,
      reason: form.reason,
    });
  }

  // Total XP to be granted, for the confirmation preview.
  // Mirrors the api-side filter: zero-amount XP rows are
  // dropped from the actual grant, so the preview should
  // match the real total.
  const totalXp = form.xp
    .filter((r) => r.amount > 0)
    .reduce((sum, r) => sum + r.amount, 0);
  // The button is enabled whenever the form COULD be
  // valid. The reason check + the "at least one positive
  // amount" check are the actual gates. Letting the
  // button stay enabled when those fail would let the
  // user click and get an error message — better UX than
  // a silently-disabled button. The empty case (reason
  // filled but no XP/coins) is the most common mistake,
  // so we show the error inline on click.
  const reasonValid = form.reason.trim().length >= 10;
  const hasValidAmount = totalXp > 0 || form.coins > 0;
  const formValid = reasonValid && hasValidAmount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="grant-title"
    >
      <div
        className="w-full max-w-lg rounded-lg border border-white/5 bg-bg-panel p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="grant-title" className="text-lg font-semibold">
          Grant XP / coins to {target.display_name}
        </h2>
        <p className="mt-1 text-xs text-ink-muted">
          Bypasses the scoring engine, the quest system, the dedup window,
          and the {target.is_ignored ? <em>(currently ignored) </em> : null}
          is_ignored flag. Recorded as <code>admin_grant</code> in the audit log.
        </p>

        <form
          onSubmit={onSubmit}
          className="mt-4 space-y-4"
          autoComplete="off"
          // data-form-type="other" + data-lpignore="true"
          // discourage password managers (LastPass,
          // 1Password) and browser autofill from
          // touching the inputs. Without these, some
          // password managers inject a 1-3 second
          // delay on input focus as they look up
          // possible matches — which is exactly the
          // 3-second pause the user reported.
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
        >
          <fieldset className="rounded-md border border-white/5 p-3">
            <legend className="px-1 text-xs uppercase tracking-wide text-ink-muted">XP grants</legend>
            <div className="space-y-2">
              {form.xp.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="input flex-1"
                    value={row.competency_id}
                    onChange={(e) => updateXpRow(idx, { competency_id: e.target.value })}
                    aria-label="Competency"
                  >
                    {(competencies.data?.competencies ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input
                    className="input w-24"
                    type="number"
                    min={0}
                    max={10_000}
                    step={1}
                    value={row.amount}
                    onChange={(e) => updateXpRow(idx, { amount: Number(e.target.value) })}
                    aria-label="XP amount"
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                  <span className="text-xs text-ink-muted">XP</span>
                  <button
                    type="button"
                    className="btn-ghost px-2"
                    onClick={() => removeXpRow(idx)}
                    aria-label="Remove row"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={addXpRow}
                disabled={!(competencies.data?.competencies ?? []).length}
              >
                + Add XP row
              </button>
            </div>
          </fieldset>

          <div className="rounded-md border border-white/5 p-3">
            <label className="label" htmlFor="grant-coins">Coins</label>
            <input
              id="grant-coins"
              className="input"
              type="number"
              min={0}
              max={1_000_000}
              step={1}
              value={form.coins}
              onChange={(e) => setForm((f) => ({ ...f, coins: Number(e.target.value) }))}
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <p className="mt-1 text-xs text-ink-dim">
              0 = no coin grant. Positive = credit the balance.
            </p>
          </div>

          <div className="rounded-md border border-white/5 p-3">
            <label className="label" htmlFor="grant-reason">Reason (required, 10–500 chars)</label>
            <textarea
              id="grant-reason"
              className="input min-h-[5rem]"
              minLength={10}
              maxLength={500}
              required
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. Manual adjustment for PR #18 missed by the rule engine"
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <p className="mt-1 text-xs text-ink-dim">
              {form.reason.length} / 500. This is the audit trail's most-searchable field.
            </p>
          </div>

          {showConfirm ? (
            <div className="rounded-md border border-accent-warn/40 bg-accent-warn/5 p-3 text-sm">
              <p className="font-medium">Confirm grant</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-ink-muted">
                {form.xp.filter((r) => r.amount > 0).map((row, idx) => {
                  const comp = (competencies.data?.competencies ?? []).find((c) => c.id === row.competency_id);
                  return (
                    <li key={idx}>
                      +{row.amount} XP to <strong>{comp?.name ?? row.competency_id}</strong>
                    </li>
                  );
                })}
                {form.coins > 0 ? <li>+{form.coins} coins</li> : null}
                <li>Reason: <em>{form.reason}</em></li>
              </ul>
              <p className="mt-2 text-xs text-ink-dim">
                This is recorded as an <code>admin_grant</code> in the audit log. The user will see it on their dashboard with a distinct &quot;admin&quot; source badge. The action is not reversible from the UI; the admin can void the resulting xp_award / coin_transaction rows if needed.
              </p>
            </div>
          ) : null}

          {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}
          {!err && !formValid && reasonValid ? (
            // The user has a reason but no valid amount.
            // Show this as a soft hint (not an error) so
            // they know what to fix before clicking.
            <p className="text-xs text-ink-muted">
              Add at least one XP row with a positive amount, or a positive coin amount.
            </p>
          ) : null}
          {!err && !formValid && !reasonValid && form.reason.length > 0 ? (
            // The user started typing a reason but it's
            // still < 10 chars. Soft hint with a counter.
            <p className="text-xs text-ink-muted">
              {form.reason.length} / 10 chars minimum for the reason.
            </p>
          ) : null}
          {grant.isError ? (
            <p className="text-sm text-accent-danger" role="alert">
              {grant.error instanceof ApiError ? grant.error.message : 'Grant failed.'}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={grant.isPending}
              title={
                formValid
                  ? showConfirm
                    ? 'Click to apply the grant'
                    : 'Click to review the grant before applying'
                  : reasonValid
                    ? 'Add at least one XP row with a positive amount, or a positive coin amount'
                    : 'Reason must be at least 10 characters'
              }
            >
              {grant.isPending
                ? 'Applying…'
                : showConfirm
                  ? `Confirm: +${totalXp} XP${form.coins > 0 ? ` + ${form.coins} 🪙` : ''}`
                  : 'Review grant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
